import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState, useEffect } from 'react';
import LogoutConfirmation from '@/components/LogoutConfirmation';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Loader2 } from "lucide-react";
import { SupabaseProvider } from "@/lib/supabase";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SectorProvider } from "@/contexts/SectorContext";
import { useMode } from "@/contexts/ModeContext"; // Add this import
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import Index from "./pages/Index";
import Analytics from "./pages/Analytics";
import Leaderboard from "./pages/Leaderboard";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Chatbot from "./pages/Chatbot";
import SectorSelection from "./pages/SectorSelection";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Register from "./pages/Register";
import { AIInsights } from '@/pages/AIInsights';
import Sidebar from "@/components/layout/Sidebar";
import Navbar from "@/components/layout/Navbar";
import Landing from "./pages/Landing";
import Predictions from '@/pages/Predictions';
import Weather from '@/pages/Weather';
import Community from '@/pages/Community';
import { Layout } from '@/components/layout';

// Simple check if the user has selected a sector already
const hasSectorSelected = () => {
  return localStorage.getItem('aquagenius-sector') !== null;
};

// Protected route component with loading state
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Layout component to wrap all pages
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const { isRTL } = useLanguage();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { mode } = useMode();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Don't show navigation elements for login, register, and sector selection pages
  const noNavPaths = ['/login', '/register', '/select-sector', '/'];
  const showNav = !noNavPaths.includes(location.pathname);

  return (
    <div className={cn(
      "min-h-screen flex transition-colors duration-300",
      `mode-${mode}`,
      "bg-[var(--app-bg)]",
      isRTL ? 'rtl' : 'ltr'
    )}>
      {showNav && isAuthenticated && (
        <Sidebar 
          collapsed={sidebarCollapsed} 
          setCollapsed={setSidebarCollapsed} 
          className="shadow-[var(--app-shadow)] dark:shadow-lg"
        />
      )}
      <div className={cn(
        "min-h-screen flex-1 flex flex-col",
        showNav && isAuthenticated && "transition-all duration-300"
      )}>
        {showNav && isAuthenticated && (
          <Navbar 
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} 
            className="sticky top-0 z-40 border-b bg-[var(--app-surface)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--app-surface)]/60 shadow-[var(--app-shadow)] dark:shadow-md"
          />
        )}
        <main className="flex-1 p-4">
          {children}
        </main>
      </div>
    </div>
  );
};

// Page transition wrapper component
const PageTransition = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
    >
      {children}
    </motion.div>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!loading) {
      setInitialized(true);
    }
  }, [loading]);

  if (!initialized) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
        <Routes>
        <Route path="/" element={<AppLayout><Outlet /></AppLayout>}>
          <Route index element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />
          } />
          <Route path="analytics" element={<PageTransition><Analytics /></PageTransition>} />
          <Route path="predictions" element={<PageTransition><Predictions /></PageTransition>} />
          <Route path="ai-insights" element={<PageTransition><AIInsights /></PageTransition>} />
          <Route path="chatbot" element={<PageTransition><Chatbot /></PageTransition>} />
          <Route path="settings" element={<PageTransition><Settings /></PageTransition>} />
          <Route path="profile" element={<PageTransition><Profile /></PageTransition>} />
          <Route path="Weather" element={<PageTransition><Weather /></PageTransition>} />
          <Route path="Community" element={<PageTransition><Community /></PageTransition>} />
        </Route>

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/select-sector" element={<SectorSelection />} />

        {/* Protected routes */}
        <Route
          path="/select-sector"
          element={
            <ProtectedRoute>
              <PageTransition>
                <SectorSelection />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Protected routes with Layout */}
        <Route element={
          <ProtectedRoute>
            <AppLayout>
              <Outlet />
            </AppLayout>
          </ProtectedRoute>
        }>
          <Route
            path="/dashboard"
            element={
              <PageTransition>
                <Index />
              </PageTransition>
            }
          />
          <Route 
            path="/leaderboard"
            element={
              <PageTransition>
                <Leaderboard />
              </PageTransition>
            }
          />
        </Route>

        {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
    </AnimatePresence>
  );
};

// Error Fallback Component
const ErrorFallback = ({ error }: { error: Error }) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-4 text-center">
        <h2 className="text-2xl font-bold text-destructive">Something went wrong</h2>
        <p className="text-muted-foreground">{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
        >
          Try again
        </button>
      </div>
    </div>
  );
};

const App = () => {
  console.log('App rendering');
  
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <SupabaseProvider>
        <AuthProvider>
          <ThemeProvider>
            <LanguageProvider>
          <SectorProvider>
                <TooltipProvider>
                  <AppRoutes />
                  <Toaster />
                  <Sonner />
                </TooltipProvider>
          </SectorProvider>
            </LanguageProvider>
          </ThemeProvider>
        </AuthProvider>
      </SupabaseProvider>
    </ErrorBoundary>
  );
};

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; FallbackComponent: React.ComponentType<{ error: Error }> },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; FallbackComponent: React.ComponentType<{ error: Error }> }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return <this.props.FallbackComponent error={this.state.error} />;
    }
    return this.props.children;
  }
}

export default App;
